import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Breadcrumb from "../../../../../components/Common/Breadcrumb"; // Adjust the import path as needed
import { withTranslation } from "react-i18next";
import { Button, Col, Row } from "reactstrap";
import ataService from "services/AtaService";
import permissionsService from "services/permissionsService";
import Loading from "components/Common/Loading";
import ModalCreateAta from "./ModalCreate"; // Adjust the import path as needed
import DynamicMaterialTable from "components/Common/MaterialTableContainer";
import useTableZoom from "components/Common/useTableZoom";
import moment from "moment";
import { IconButton, Tooltip, Box } from "@mui/material";
import { Plus, Trash } from "@phosphor-icons/react";
import ModalExcludeAta from "./ModalExcludeAta";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
} from "components/Common/MaterialTableStyles";

const Atas = () => {
	// ========== CONFIGURAÇÃO DE LARGURAS MÍNIMAS E MÁXIMAS DAS COLUNAS ==========
	const COLUMN_WIDTHS = {
		name: { min: 200 },                    // Largura mínima da coluna "Nome da Ata" (sem máximo - cresce livremente)
		create_at: { min: 120, max: 120 },     // Largura mín/máx da coluna "Data" (em pixels)
		actions: { min: 60, max: 60 },         // Largura mín/máx da coluna "Actions" (em pixels)
	};
	// ================================================================

	const [data, setData] = useState([]);
	const [temPermissaoVisualizar, setTemPermissaoVisualizar] = useState(false);
	const [temPermissaoAdicionar, setTemPermissaoAdicionar] = useState(false);
	const [temPermissaoEditar, setTemPermissaoEditar] = useState(false);
	const [temPermissaoExcluir, setTemPermissaoExcluir] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false); // State to manage modal open/close
	const [dataModalExcludeAta, setDataModalExcludeAta] = useState(null);
	const [excludeAtaOpenModal, setExcludeAtaOpenModal] = useState(false);

	const navigate = useNavigate();

	document.title = "Senda | Atas";

	// Usar o hook customizado para zoom e cálculos de largura
	const { zoomLevel, getColumnWidth } = useTableZoom(COLUMN_WIDTHS);

	const columns = useMemo(
		() => [
			{
				header: "Nome da Ata",
				accessorKey: "name",
				filterFn: "contains",
				widthPixels: getColumnWidth("name"),
				minWidthPixels: COLUMN_WIDTHS.name.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
				Cell: ({ cell, row }) => (
					<span
						style={{
							cursor: "pointer",
							display: "block",
							maxWidth: "100%",
							wordBreak: "break-word",
							overflowWrap: "break-word",
							padding: "3px",
							lineHeight: "1.2",
						}}
						onMouseEnter={(e) => {
							e.target.style.textDecoration = "underline";
						}}
						onMouseLeave={(e) => {
							e.target.style.textDecoration = "none";
						}}
						onClick={() => editarItem(row.original)}
					>
						{cell.getValue()}
					</span>
				),
			},
			{
				header: "Data",
				accessorKey: "create_at",
				// filterFn: "contains",
				widthPixels: getColumnWidth("create_at"),
				minWidthPixels: COLUMN_WIDTHS.create_at.min,
				maxWidthPixels: COLUMN_WIDTHS.create_at.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				accessorFn: (originalRow) => new Date(originalRow.create_at),
				filterVariant: "date",
				Cell: ({ cell }) => (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: "100%",
							height: "100%",
							wordBreak: "break-word",
							overflowWrap: "break-word",
						}}
					>
						<span>
							{new Date(cell.getValue()).toLocaleDateString(
								"pt-BR",
							)}
						</span>
					</div>
				),
			},
			{
				accessorKey: "actions",
				widthPixels: getColumnWidth("actions"),
				minWidthPixels: COLUMN_WIDTHS.actions.min,
				maxWidthPixels: COLUMN_WIDTHS.actions.max,
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => (
					<div style={{
						display: "flex",
						width: "100%",
						alignItems: "center",
						justifyContent: "center",
					}}>
						{temPermissaoExcluir && (
							<div>
								<Tooltip title="Excluir" placement="top">
									<IconButton
										style={{
											background: "none",
											border: "none",
											padding: "1px",
											cursor: "pointer",
											color: "black",
											minWidth: "auto",
											width: "auto",
											height: "auto",
										}}
										onClick={() =>
											deletarItem(row.original)
										}
									>
										<Trash size={14} />
									</IconButton>
								</Tooltip>
							</div>
						)}
					</div>
				),
				align: "center",
			},
		],
		[temPermissaoExcluir, zoomLevel], // Adicionado zoomLevel como dependência
	);

	const deletarItem = (rowData) => {
		setDataModalExcludeAta(rowData);
		setExcludeAtaOpenModal(true);
	};

	const handleCloseModalExcludeAta = () => {
		setExcludeAtaOpenModal(false);
		getItems();
	};

	const handleDeleteAta = async (id) => {
		try {
			setIsLoading(true);
			await ataService.delete(id);
			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Ata desativada com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			await SendaCustomSwal.fire({
				title: "Erro",
				text: error.message,
				iconHtml: '<i class="error-icon"></i>',
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
		handleCloseModalExcludeAta();
	};

	const editarItem = (rowData) => {
		navigate(`/form-atas/${rowData.id}`);
	};

	const getItems = async () => {
		try {
			const response = await ataService.get();
			setData(response);
		} catch (error) {
			console.log(error);
		}
	};

	const getAll = async () => {
		setIsLoading(true);
		await getPermissions();
		await getItems();
		setIsLoading(false);
	};

	useEffect(() => {
		getAll();
	}, []);

	const getPermissions = async () => {
		try {
			const response = await permissionsService.getPermissions();
			for (let permission of response) {
				if (permission.role === "ataList") {
					setTemPermissaoVisualizar(true);
				}
				if (permission.role === "ataUpdate") {
					setTemPermissaoAdicionar(true);
				}
				if (permission.role === "ataCreate") {
					setTemPermissaoAdicionar(true);
				}
				if (permission.role === "ataDelete") {
					setTemPermissaoExcluir(true);
				}
			}
		} catch (error) {
			console.log(error);
		}
	};

	const toggleModal = () => {
		setIsModalOpen(!isModalOpen);
	};

	if (!temPermissaoVisualizar) return null;

	return (
		<Loading isLoading={isLoading}>
			<div className="page-content">
				<div
					className="container-fluid"
					style={{
						backgroundColor: "white",
						padding: 20,
						borderRadius: 10,
						marginTop: 25,
					}}
				>
					<Col
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<h4
							style={{
								fontSize: "18px",
								fontWeight: "bold",
							}}
						>
							Atas
						</h4>

						{temPermissaoAdicionar && (
							<Button
								style={{
									background: "none",
									color: "black",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: "2px",
									marginBottom: 20,
								}}
								onClick={toggleModal}
							>
								<Plus size={16} /> Adicionar Ata
							</Button>
						)}
					</Col>

					<DynamicMaterialTable
						columns={columns}
						data={data}
						renderTopToolbarCustomActions={({ table }) => (
							<Box
								sx={{
									display: "flex",
									justifyContent: "flex-end",
									alignItems: "center",
									width: "100%",
									marginTop: "3px",
								}}
							>
								{table.getFilteredRowModel().rows.length > 0 && (
									<Box sx={{ fontWeight: "500", color: "#495057" }}>
										Total <span style={{ color: "#000" }}>{table.getFilteredRowModel().rows.length}</span> itens.
									</Box>
								)}
							</Box>
						)}
					/>
				</div>
			</div>
			<ModalCreateAta isOpen={isModalOpen} toggle={toggleModal} />{" "}
			<ModalExcludeAta
				show={excludeAtaOpenModal}
				data={dataModalExcludeAta}
				onCloseClick={handleCloseModalExcludeAta}
				onDeleteClick={handleDeleteAta}
			/>
			{/* Add the modal component */}
		</Loading>
	);
};

Atas.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default withTranslation()(Atas);
